import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./common.js";
import { API_KEY_SCOPES } from "../constants/index.js";

/**
 * 진실의 원천: harness/API-SPEC.md §12, harness/SECURITY.md §4.
 * **평문 키는 발급 응답 한 번에만 실린다.** 목록에도 조회에도 다시 나오지 않는다.
 */

export const ApiKeySchema = z.object({
  id: IdSchema,
  name: z.string(),
  scope: z.enum(API_KEY_SCOPES),
  created_at: IsoDateTimeSchema,
  last_used_at: IsoDateTimeSchema.nullable(),
  revoked_at: IsoDateTimeSchema.nullable(),
});
export type ApiKey = z.infer<typeof ApiKeySchema>;

/**
 * 지금 **실제로 통하는 경로가 있는** 스코프.
 *
 * 규칙은 하나다 — **경로가 있어야 발급한다.** "만들었는데 아무 데도 안 되는 키" 는
 * 선언만 하고 안 쓰는 필터와 같은 종류의 거짓말이다(결정 C38·C45).
 *
 * - `ingest` : 수집기 수신 경로 (C44)
 * - `read`   : 지식 API `/api/kb/*` 가 생기면서 열었다 (C67, Phase 4-A).
 *              **키는 발급한 사람의 스코프를 물려받는다**(migrations/0004) — 사람에게
 *              걸린 제한이 키로 우회되면 스코프는 없는 것과 같다
 * - `admin`  : 아직 경로가 없다. 설정값 조회(`/api/kb/env`·`/api/v1/env`)가 생기면 연다
 */
export const ISSUABLE_API_KEY_SCOPES = ["ingest", "read"] as const;

export const ApiKeyCreateRequestSchema = z.object({
  name: z.string().trim().min(2),
  scope: z.enum(ISSUABLE_API_KEY_SCOPES),
});
export type ApiKeyCreateRequest = z.infer<typeof ApiKeyCreateRequestSchema>;

/** 발급 직후에만 쓰는 모양 — `key` 는 이 응답 이후 어디에도 없다 */
export const ApiKeyCreatedSchema = ApiKeySchema.extend({
  key: z.string().min(1),
});
export type ApiKeyCreated = z.infer<typeof ApiKeyCreatedSchema>;

export const ApiKeyListResponseSchema = z.object({
  items: z.array(ApiKeySchema),
});
export type ApiKeyListResponse = z.infer<typeof ApiKeyListResponseSchema>;
